<template>
	<div class="flex flex-col">
		<InputText class="!mb-0" v-if="disabled" :label="props.title" type="text" label-text-size="text-sm" :input-height="props.inputHeight"
					  font-weight="font-normal" v-model="selectedItem" :disabled="true" :icon="icon"/>

		<Combobox v-else as="div" v-model="selectedItem" @update:modelValue="handleSelection">
			<div ref="comboboxRef">
				<ComboboxLabel v-if="props.title" class="leading-6 mb-2 block text-label" :class="labelCustomClass">{{ props.title }}</ComboboxLabel>
				<div class="group relative">
					<ComboboxInput :maxLength="props.maxLength" :readonly="props.readonly" autocomplete="off" :onclick="comboBoxClicked"
										class="block w-full font-medium rounded bg-neutral py-4 pl-4 pr-12 border text-label outline-none placeholder:text-placeholder
										focus:ring-1 focus:ring-primary-bold-hover focus:border-primary-bold-hover
										group-hover:ring-1 group-hover:ring-primary-bold-hover group-hover:border-primary-bold-hover"
										:class="comboBoxCustomClass"
										@change="query = $event.target.value" @blur="query = ''" :display-value="(item) => item" :placeholder="props.placeholder" />

					<ComboboxButton :id="props.id" class="absolute inset-y-0 right-0 flex items-center rounded-r px-2 outline-none">
						<template #default="{ open }">
							<ChevronUpIcon v-if="open" class="size-5 text-icon-neutral group-hover:text-primary-hover focus:text-primary-hover" aria-hidden="true" />
							<ChevronDownIcon v-else class="size-5 text-icon-neutral group-hover:text-primary-hover focus:text-primary-hover" aria-hidden="true" />
						</template>
					</ComboboxButton>

					<ComboboxOptions v-if="props.enterable || filteredItems.length > 0"
										  class="absolute z-10 mt-2 max-h-60 w-full overflow-auto rounded bg-neutral mb-2 text-base font-medium ring-1 ring-primary-bold-hover border border-primary-bold-hover focus:outline-none sm:text-sm"
										  :class="{ 'top-full': !showOptionsOnTop, 'bottom-full': showOptionsOnTop }">

						<!-- Shows when user enters custom value -->
						<ComboboxOption v-if="props.enterable && queryItem" :value="queryItem"
											 class="text-label py-2 pl-3 pr-3 h-9 border border-primary-bold-hover rounded whitespace-nowrap">
							Create: "{{ query }}"
						</ComboboxOption>

						<ComboboxOption v-for="item in filteredItems" :key="item" :value="item" as="template" v-slot="{ active, selected }">
							<li :class="['relative cursor-default select-none py-2 pl-3 pr-9 h-9', active ? 'bg-primary-subtle text-label outline-none' : 'text-placeholder']">
								<span :class="['block truncate', selected && 'text-label']">
									{{ item }}
								</span>

								<span v-if="selected" class="absolute inset-y-0 right-0 flex items-center pr-4 text-label">
									<CheckIcon class="size-5" aria-hidden="true" />
							  </span>
							</li>
						</ComboboxOption>
					</ComboboxOptions>
				</div>
			</div>
		</Combobox>
		<ValidationError :errors="props.errors" :field="props.errorField" class="mt-2 ml-3.5" />
	</div>
</template>

<script setup lang="js">
/*
	Ref: https://headlessui.com/v1/vue/combobox

	Changes:
		2025-10-09 - Added enterable property and supporting code to allow user to add custom data;
 */

import { computed, ref, defineModel } from 'vue'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/20/solid'
import {
	Combobox,
	ComboboxButton,
	ComboboxInput,
	ComboboxLabel,
	ComboboxOption,
	ComboboxOptions
} from '@headlessui/vue'
import ValidationError from '@/components/Forms/InputError.vue'
import { ApiGatewayResponse } from '@/api/ApiGateway.js'
import InputText from '@/components/Forms/InputText.vue'

const emit = defineEmits(['onChange'])

const props = defineProps({
	id: { type: String, required: true },
	items: { type: Array, default: () => [] },
	title: { type: String, default: '' },
	placeholder: { type: String, default: '' },
	showOptionsOnTop: { type: Boolean, default: false },
	inputHeight: { type: String, default: '' },
	labelTextSize: { type: String, default: '' },
	labelClass: { type: String, default: '' },//AJL
	errors: { type: ApiGatewayResponse, default: null },
	errorField: { type: String, default: null },
	readonly: { type: Boolean, default: false },
	disabled: { type: Boolean, default: false },
	enterable: { type: Boolean, default: false }, //Set true to allow user to enter custom values (readonly must be false)
	icon: { type: String, default: 'none' },
	maxLength: { type: Number, default: 255 },
})

const selectedItem = defineModel()

const labelCustomClass = computed(() =>
{
	return [props.labelClass, props.labelTextSize].join(' ')
})

const comboBoxCustomClass = computed(() =>
{
	const classes = [props.inputHeight];
	classes.push(hasError() ? 'border-error' : 'border-neutral');

	if (props.readonly)
	{
		classes.push('cursor-pointer')
	}

	return classes.join(' ')
})

const query = ref('')

/**
 * Used to show "Create" option when user is entering a custom value
 * Ref: https://headlessui.com/v1/vue/combobox#allowing-custom-values
 */
const queryItem = computed(() => {
	return query.value === '' ? null : query.value
})

const filteredItems = computed(() =>
	query.value === ''
		? props.items
		: props.items.filter((item) =>
		{
			return item.toLowerCase().includes(query.value.toLowerCase())
		})
)
const comboboxRef = ref(null)

function hasError()
{
	return props.errors?.validationErrors()?.find((error) => error.source === props.errorField) != null
}

function comboBoxClicked()
{
	if (props.readonly)
	{
		document.getElementById(props.id).click()
	}
}

function handleSelection(newValue) {
	query.value = ''
	emit('onChange', newValue)
}

</script>